import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useInvoicePdf } from "../hooks/use-invoice";
import { Download, Eye, Tag, CreditCard, Calendar, Hash } from "lucide-react";

interface Invoice {
  invoice_number: string;
  user_full_name: string;
  user_email?: string;
  amount_paid: number;
  status: "ENQUEUED" | "ISSUED" | "PARTIALLY_REFUNDED" | "REFUNDED" | "VOID";
  currency?: string;
  product_name: string;
  bundle_name: string;
  sessions_count?: number;
  payment_method: string;
  payment_date: string;
  payment_source_id: string;
  actual_price: number;
  discount_amount?: number;
  coupon_code?: string;
  created_at: string;
  id: string;
}

interface InvoiceCardProps {
  invoice: Invoice;
  onCardClick?: () => void;
}

const statusConfig: Record<
  Invoice["status"],
  { label: string; className: string }
> = {
  ENQUEUED: {
    label: "Enqueued",
    className: "bg-amber-50 text-amber-700 border border-amber-200",
  },
  ISSUED: {
    label: "Issued",
    className: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  },
  PARTIALLY_REFUNDED: {
    label: "Partial Refund",
    className: "bg-sky-50 text-sky-700 border border-sky-200",
  },
  REFUNDED: {
    label: "Refunded",
    className: "bg-violet-50 text-violet-700 border border-violet-200",
  },
  VOID: {
    label: "Void",
    className: "bg-red-50 text-red-600 border border-red-200",
  },
};

export function InvoiceCard({ invoice, onCardClick }: InvoiceCardProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const { mutateAsync: getPdf, isPending } = useInvoicePdf();

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: invoice.currency || "INR",
      minimumFractionDigits: 0,
    }).format(amount / 100);

  const handlePreview = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const blob = await getPdf(invoice.id);
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (error) {
      console.error("Preview failed", error);
    }
  };

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const blob = await getPdf(invoice.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${invoice.invoice_number}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed", error);
    }
  };

  const closePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const status = statusConfig[invoice.status];
  const hasDiscount = !!invoice.discount_amount;

  return (
    <>
      <Card
        onClick={onCardClick}
        className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow duration-200 border border-border"
      >
        <div className="flex items-start justify-between px-5 pt-5 pb-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-mono font-semibold text-foreground tracking-tight">
                {invoice.invoice_number}
              </span>
              <Badge
                className={cn(
                  "text-[11px] font-medium px-2 py-0.5 rounded-full",
                  status.className
                )}
              >
                {status.label}
              </Badge>
            </div>
            <p className="text-sm font-medium text-foreground mt-1 truncate">
              {invoice.user_full_name}
            </p>
            {invoice.user_email && (
              <p className="text-xs text-muted-foreground truncate">
                {invoice.user_email}
              </p>
            )}
          </div>

          <div className="text-right ml-4 shrink-0">
            <p className="text-xl font-bold text-foreground tabular-nums">
              {formatCurrency(invoice.amount_paid)}
            </p>
            {hasDiscount && (
              <p className="text-xs text-muted-foreground line-through tabular-nums">
                {formatCurrency(invoice.actual_price)}
              </p>
            )}
          </div>
        </div>

        <div className="px-5 py-3 bg-muted/40 border-y border-border">
          <p className="text-sm font-medium text-foreground truncate">
            {invoice.product_name}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {invoice.bundle_name}
            {invoice.sessions_count != null &&
              ` · ${invoice.sessions_count} sessions`}
          </p>
        </div>

        <div className="px-5 py-3 flex flex-wrap gap-x-5 gap-y-1.5">
          <MetaChip icon={<CreditCard className="w-3 h-3" />} label={invoice.payment_method} />
          <MetaChip
            icon={<Calendar className="w-3 h-3" />}
            label={format(new Date(invoice.payment_date), "MMM d, yyyy · h:mm a")}
          />
          <MetaChip
            icon={<Hash className="w-3 h-3" />}
            label={invoice.payment_source_id}
            truncate
          />
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-border">
          <div className="flex items-center gap-3 flex-wrap">
            {!hasDiscount && (
              <span className="text-xs text-muted-foreground">
                <span className="text-foreground font-medium">Price:</span>{" "}
                {formatCurrency(invoice.actual_price)}
              </span>
            )}
            {hasDiscount && (
              <span className="text-xs text-muted-foreground">
                <span className="text-foreground font-medium">Discount:</span>{" "}
                <span className="text-emerald-600 font-medium">
                  −{formatCurrency(invoice.discount_amount!)}
                </span>
              </span>
            )}
            {invoice.coupon_code && (
              <span className="inline-flex items-center gap-1 text-xs font-mono bg-muted text-muted-foreground px-2 py-0.5 rounded">
                <Tag className="w-3 h-3" />
                {invoice.coupon_code}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 ml-3 shrink-0">
            <Button
              size="icon"
              variant="outline"
              className="h-8 w-8"
              onClick={handlePreview}
              disabled={isPending}
              title="Preview invoice"
            >
              <Eye className="w-3.5 h-3.5" />
            </Button>
            <Button
              size="icon"
              className="h-8 w-8"
              onClick={handleDownload}
              disabled={isPending}
              title="Download PDF"
            >
              <Download className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </Card>

      <Dialog open={!!previewUrl} onOpenChange={closePreview}>
        <DialogContent className="max-w-5xl w-[90vw] h-[85vh] top-10 translate-y-0">
          <div className="flex flex-col h-full">
            <DialogHeader>
              <DialogTitle className="font-mono">{invoice.invoice_number}</DialogTitle>
            </DialogHeader>
            {previewUrl && (
              <iframe
                src={previewUrl}
                className="flex-1 w-full rounded-md border mt-4"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function MetaChip({
  icon,
  label,
  truncate,
}: {
  icon: React.ReactNode;
  label: string;
  truncate?: boolean;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 text-xs text-muted-foreground",
        truncate && "max-w-[180px] truncate"
      )}
    >
      <span className="text-muted-foreground/60 shrink-0">{icon}</span>
      <span className={truncate ? "truncate" : undefined}>{label}</span>
    </span>
  );
}